import { createContext, use, useCallback, useMemo, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { Toast, ToastRegion } from 'armxlib'
import type { FeedbackTone } from 'armxlib'

interface ToastItem {
  id: number
  text: string
  tone: FeedbackTone
}

/** Shows a short confirmation at the top of the screen. */
export type ShowToast = (text: string, tone?: FeedbackTone) => void

const ToastContext = createContext<ShowToast>(() => {})

/** Region where toasts appear; keeps it mounted so screen readers announce them. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const show = useCallback<ShowToast>((text, tone = 'success') => {
    setToasts((current) => [...current, { id: Date.now() + Math.random(), text, tone }])
  }, [])
  const dismiss = useCallback((id: number) => setToasts((current) => current.filter((toast) => toast.id !== id)), [])
  const value = useMemo(() => show, [show])

  return (
    <ToastContext value={value}>
      {children}
      <ToastRegion placement="top" style={{ '--armx-toast-offset': '4.5rem' } as CSSProperties}>
        {toasts.map((toast) => (
          <Toast key={toast.id} tone={toast.tone} duration={3000} onDismiss={() => dismiss(toast.id)}>
            {toast.text}
          </Toast>
        ))}
      </ToastRegion>
    </ToastContext>
  )
}

/**
 * The function that shows a toast.
 * @returns A function taking the text and its tone.
 */
export function useToast(): ShowToast {
  return use(ToastContext)
}
